import { parsePhoneNumberFromString } from "libphonenumber-js";
import _ from "lodash";
import { DateTime } from "luxon";

import { EQUIPMENT_VALUE_TO_LABEL_MAP, EVOTRUX_URL_HASH } from "../constants";
import { getQuoteNormalizedPrice } from "./quotes";
import { sortByOldest } from "./time";
import { MAIN_CONTENT_ELEMENT_ID } from "../layouts/constants";

import type { SearchBoxFeatureSuggestion } from "@mapbox/search-js-core";
import type { AxiosError } from "axios";
import type { KeyboardEvent } from "react";

export { getRegionForCoordinates } from "./atlas";
export * from "./carrierStats";
export * from "./carrierUpgradeAnalytics";
export * from "./carrierVerification";
export * from "./chat";
export * from "./equipmentValidation";
export * from "./fileUpload";
export * from "./localStorageUtils";
export * from "./offPlatformBooking";
export * from "./directBookingAssignment";
export * from "./quotes";
export * from "./shipment";
export * from "./shipperCompanyVisibility";
export * from "./subscription";
export * from "./support";
export * from "./swr/swr";
export * from "./currency";
export * from "./tfc";
export * from "./time";

// TODO: Refactor util functions in index to seperate files

export function convertSubdivisionCodeToSimplifiedSubdivisionCode(
  code: string,
): string {
  /**
   * Returns the subdivision-specific part ('MB') in 'CA-MB'
   * from an ISO 3166-2 subdivision code
   */
  return code.includes("-") ? code.split("-")[1] : code;
}

export function capitalizeFirst(words: string): string {
  return words
    .toLowerCase()
    .split(" ")
    .map((word) => (word[0] ?? "").toUpperCase() + word.substring(1))
    .join(" ");
}

export function formatText(text: string): string {
  return text.toLowerCase().split("_").join(" ");
}

export function getFullName(firstName?: string, lastName?: string): string {
  return [firstName, lastName].filter(Boolean).join(" ");
}

export function userBelongsToCompany(user: User | null): boolean {
  if (user == null) {
    return false;
  }

  return user.roles.some((role) => role?.company != null);
}

export function validateEmail(email: string): string | null {
  // Regex source: https://regexr.com/
  const emailRegEx = /^[\w.+-]+@([\w-]+\.)+[\w-]{2,4}$/;
  return emailRegEx.test(email) ? null : "Invalid email format";
}

export function emailIsValid(email: string): boolean {
  if (validateEmail(email) == null) {
    return true;
  }
  return false;
}

export function formatNumberToDecimal(
  value: number | string,
  decimals = 2,
): string {
  return parseFloat(value.toString()).toFixed(decimals);
}

export function getRouteOrigin(routePoints: RoutePoint[]): RoutePoint | null {
  let point = null;
  routePoints.forEach((rp) => {
    if (rp.stop_type === "ORIGIN") {
      point = rp;
    }
  });
  return point;
}

export function getRouteDestination(
  routePoints: RoutePoint[],
): RoutePoint | null {
  let point = null;
  routePoints.forEach((rp) => {
    if (rp.stop_type === "DESTINATION") {
      point = rp;
    }
  });
  return point;
}

export function userHasAdminRoleInCompany(user: User): boolean {
  return user.roles.some(
    (role: Role) => role.type === "ADMINISTRATOR" && role.company != null,
  );
}

function lbToKg(number: number): number {
  return number * 0.45359237;
}

interface FormatWeightLbOptions {
  includeKg?: boolean;
}

export function formatWeightLb(
  weightLb: number | string,
  options: FormatWeightLbOptions = {},
): string {
  const { includeKg = true } = options;
  const pounds = typeof weightLb === "number" ? weightLb : parseFloat(weightLb);
  const lb = `${pounds.toFixed(1)} lb`;
  return includeKg ? `${lb} (${lbToKg(pounds).toFixed(1)} kg)` : lb;
}

export const toFullReadableAddress = (
  location?: Partial<RoutePointLocation>,
): string => {
  if (!location) {
    return "";
  }

  const parts: string[] = [];

  // Don't include business name if it is the same as the address or city
  if (
    location?.business_name &&
    location.business_name !== location?.address &&
    location.business_name !== location?.city
  ) {
    parts.push(location.business_name);
  }

  // Address check: Avoid duplicate city
  if (location.address && location.address !== location.city) {
    parts.push(location.address);
  }

  // City (always included if present)
  if (location.city) {
    parts.push(location.city);
  }

  // Province check: Avoid duplicate postal code
  const simplifiedProvince = location.province
    ? convertSubdivisionCodeToSimplifiedSubdivisionCode(location.province)
    : "";
  if (location.province && location.postal !== simplifiedProvince) {
    parts.push(simplifiedProvince);
  }

  // Postal Code
  if (location.postal && location.postal !== simplifiedProvince) {
    parts.push(location.postal);
  }

  // Country (always included if present)
  if (location.country) {
    parts.push(location.country);
  }

  return parts.join(", ");
};

export const toCityProvinceCountryAndZipReadablePointLocation = (
  location?: Partial<RoutePointLocation>,
  includeBusinessName = false,
  includePostal = true,
): string => {
  if (!location) {
    return "";
  }

  const parts: string[] = [];

  // Don't include business name if it is the same as the address or city
  if (
    includeBusinessName &&
    location?.business_name &&
    location.business_name !== location?.address &&
    location.business_name !== location?.city
  ) {
    parts.push(location.business_name);
  }

  if (location.city) {
    parts.push(location.city);
  }

  // Province check: Avoid duplicate postal code
  const simplifiedProvince = location.province
    ? convertSubdivisionCodeToSimplifiedSubdivisionCode(location.province)
    : "";

  if (location.province && location.postal !== simplifiedProvince) {
    parts.push(simplifiedProvince);
  }

  // Postal Code
  if (
    includePostal &&
    location.postal &&
    location.postal !== simplifiedProvince
  ) {
    parts.push(location.postal);
  }

  if (location.country) {
    parts.push(location.country);
  }

  return parts.join(", ");
};

function sortByPrice(quotes: ShipperCondensedQuote[]): ShipperCondensedQuote[] {
  return quotes.sort((a, b) => {
    const aNormalizedPrice = getQuoteNormalizedPrice(a);
    const bNormalizedPrice = getQuoteNormalizedPrice(b);
    return aNormalizedPrice - bNormalizedPrice;
  });
}

export function shipperSortQuotes(
  quotes: ShipperCondensedQuote[],
  sortBy: QuoteSortType,
): ShipperCondensedQuote[] {
  if (sortBy === "ALL") {
    return quotes;
  }
  if (sortBy === "LATEST" || sortBy === "OLDEST") {
    if (sortBy === "OLDEST") {
      const sortedByOldest = sortByOldest(quotes);
      return sortedByOldest;
    }
    const sortByLatest = sortByOldest(quotes).reverse();

    return sortByLatest;
  }
  if (sortBy === "HIGHEST_AMOUNT" || sortBy === "LOWEST_AMOUNT") {
    const unsortableQuotes = quotes.filter((q) => q.price == null);
    const sortableQuotes = quotes.filter((q) => q.price != null);

    const orderedByPriceLowestFirst = sortByPrice(sortableQuotes);

    if (sortBy === "HIGHEST_AMOUNT") {
      const orderedByPriceHighestFirst = orderedByPriceLowestFirst.reverse();
      // add unsortable quotes
      orderedByPriceHighestFirst.push(...unsortableQuotes);
      return orderedByPriceHighestFirst;
    }

    // add unsortable quotes
    orderedByPriceLowestFirst.push(...unsortableQuotes);
    return orderedByPriceLowestFirst;
  }
  // filter not implemented
  return quotes;

  // first order using filter type
  // then deal with status
}

/**
 * Enhanced sorting function for table columns with proper currency normalization
 */
export function shipperSortQuotesByColumn(
  quotes: ShipperCondensedQuote[],
  column: string,
  direction: "asc" | "desc",
): ShipperCondensedQuote[] {
  if (column === "price") {
    const unsortableQuotes = quotes.filter((q) => q.price == null);
    const sortableQuotes = quotes.filter((q) => q.price != null);

    const sortedQuotes = sortableQuotes.sort((a, b) => {
      const aNormalizedPrice = getQuoteNormalizedPrice(a);
      const bNormalizedPrice = getQuoteNormalizedPrice(b);

      if (direction === "asc") {
        return aNormalizedPrice - bNormalizedPrice;
      }
      return bNormalizedPrice - aNormalizedPrice;
    });

    // Add unsortable quotes at the end
    sortedQuotes.push(...unsortableQuotes);
    return sortedQuotes;
  }

  if (column === "pickup") {
    const unsortableQuotes = quotes.filter((q) => !q.pickup_start);
    const sortableQuotes = quotes.filter((q) => q.pickup_start);

    const sortedSortable = [...sortableQuotes].sort((a, b) => {
      const aDate = new Date(a.pickup_start).getTime();
      const bDate = new Date(b.pickup_start).getTime();

      if (direction === "asc") {
        return aDate - bDate;
      }
      return bDate - aDate;
    });

    return sortedSortable.concat(unsortableQuotes);
  }

  if (column === "delivery") {
    const unsortableQuotes = quotes.filter((q) => !q.delivery_eta);
    const sortableQuotes = quotes.filter((q) => q.delivery_eta);

    const sortedSortable = [...sortableQuotes].sort((a, b) => {
      const aDate = new Date(a.delivery_eta).getTime();
      const bDate = new Date(b.delivery_eta).getTime();

      if (direction === "asc") {
        return aDate - bDate;
      }
      return bDate - aDate;
    });

    return sortedSortable.concat(unsortableQuotes);
  }

  // Default: return shallow copy
  return [...quotes];
}

export function removeDecimalPoint(value: number): number {
  return Math.trunc(value);
}

export function meterToMile(meter: number): number {
  return meter / 1609;
}

export function meterToKilometer(meter: number): number {
  return meter / 1000;
}

const NAVIGATION_ROLLOUT_DATE = DateTime.fromObject({
  year: 2025,
  month: 3,
  day: 10,
});

export function isRouteCreatedAfterNavigationRollout(
  routeCreatedAt: string | null | undefined,
): boolean {
  if (!routeCreatedAt) {
    return true;
  }
  return DateTime.fromISO(routeCreatedAt) > NAVIGATION_ROLLOUT_DATE;
}

export function getCurrencyName(currency: number | string): string {
  if (currency === "CAD" || currency === "USD") {
    return currency;
  }

  if (currency === 1) {
    return "CAD";
  }
  if (currency === 2) {
    return "USD";
  }
  return "?";
}

export function getPageCount(count: number, itemsPerPage: number): number {
  return Math.ceil(count / itemsPerPage);
}

export const SHIPMENTS_PAGE_SIZE = 10;

export function extractCommaSeparatedEmails(emails: string): string[] {
  return emails.replace(/\s+/g, "").split(/[,]/);
}

export function emailsAreValid(emails: string[]): boolean {
  return emails.every((email) => validateEmail(email) === null);
}

export function getFirstInvalidEmail(emails: string[]): string | undefined {
  const invalidEmail = emails.find((email) => validateEmail(email) !== null);
  return invalidEmail ?? undefined;
}

export function cleanEmails(emails: string[]): string[] {
  /*
  Removes white spaces, upper case characters and angle brackets from each element
  */
  return emails.map((email) =>
    email.trim().toLowerCase().replace("<", "").replace(">", ""),
  );
}

export function sortShipmentsByDate(
  shipments: ShipperCondensedShipment[],
): ShipperCondensedShipment[] {
  return _.sortBy(shipments, "info.created_at").reverse();
}

export function sortSupplierShipmentsByDate(
  shipments: SupplierShipment[],
): SupplierShipment[] {
  return _.sortBy(shipments, "info.created_at").reverse();
}

function stringHasWhiteSpace(inputEmail: string) {
  return inputEmail.includes(" ");
}

function replaceChar(charToReplace: string, newChar: string, source: string) {
  const charIndex = source.indexOf(charToReplace);
  return `${source.substring(0, charIndex)}${newChar}${source.substring(charIndex + 1)}`;
}

export function rebuildEmailAlias(email: string | null) {
  if (email == null) {
    return null;
  }

  if (stringHasWhiteSpace(email)) {
    return replaceChar(" ", "+", email);
  }
  return email;
}

export function renderInfo(field: undefined | string | null) {
  return field !== undefined && field != null && field.length > 0;
}

export function getEquipmentTypesDisplay(equipmentTypes: string[]) {
  return equipmentTypes
    .map((equipmentType) => {
      const label = EQUIPMENT_VALUE_TO_LABEL_MAP[equipmentType];
      if (label !== undefined) {
        return label;
      }
      const isEnumValue = /^[A-Z0-9_]+$/.test(equipmentType);
      return isEnumValue
        ? capitalizeFirst(formatText(equipmentType))
        : equipmentType;
    })
    .join(", ");
}

export function isValidHttpUrl(url: string) {
  try {
    const newUrl = new URL(url);
    return newUrl.protocol === "https:" && url.includes(".");
  } catch {
    return false;
  }
}

export function isInvalidNumber(number: string | number) {
  return number === "" || Number.isNaN(Number(number));
}

export function userHasNeverLoggedIn(role: UserRoleInfo) {
  return role.user.last_login !== undefined && role.user.last_login == null;
}

// An admin-added partner is an individually named carrier just like a shipper-added
// one, so both sources belong in the copy/template carry-over.
const DIRECT_INVITE_ACL_SOURCES: ShipmentACLSource[] = [
  "DIRECT_INVITE",
  "ADMIN_DIRECT_INVITE",
];

export function filterDirectInviteCarrierACLs(
  carrierACLs: ShipmentACL[],
): ShipmentACL[] {
  return carrierACLs.filter((acl) =>
    DIRECT_INVITE_ACL_SOURCES.includes(acl.source),
  );
}

function shipperIsExpiredTrialShipper(subscription: CondensedSubscription) {
  return (
    subscription.subscription_plan === "SHIPPER_TRIAL" &&
    subscription?.shipper_trial_detail?.is_active === false
  );
}

export function getCompanyProfilePath(companyId: number): string {
  return `/companies/${companyId}/profile`;
}

export function getRoutePointLocationFromMapBoxFeature(
  feature: SearchBoxFeatureSuggestion,
): Partial<RoutePointLocation> {
  // Set business name to blank if same name as city or address
  const hasDuplicateBusinessName =
    feature.properties.name === feature.properties.address ||
    feature.properties.name === feature.properties.context.place?.name;

  return {
    longitude: feature.geometry.coordinates[0],
    latitude: feature.geometry.coordinates[1],
    address: feature.properties.address,
    country: feature.properties.context.country?.country_code,
    province: feature.properties.context.region?.region_code_full,
    city: feature.properties.context.place?.name,
    postal: feature.properties.context.postcode?.name,
    addressReadable: feature.properties.full_address,
    business_name: hasDuplicateBusinessName ? "" : feature.properties.name,
    feature,
  };
}

export function inLocalDevEnvironment() {
  return (
    import.meta.env.REACT_APP_API_URL === undefined ||
    import.meta.env.REACT_APP_API_URL.startsWith("http://localhost")
  );
}

export function sortRoutePointETAsByRoutePointAndMostRecentlyCreated(
  routePointETAs: RoutePointETA[],
): RoutePointETA[] {
  return _.orderBy(
    routePointETAs,
    [
      (item) => item.point?.point_number ?? -Infinity,
      (item) => {
        const millis = DateTime.fromISO(item.created_at).toMillis();
        return millis;
      },
    ],
    ["desc", "desc"],
  );
}

/**
 * Whether this plan has to be upgraded before the analytics dashboards are
 * available at all — as opposed to a plan that includes them but has not had
 * them switched on, which is a different message to the customer.
 *
 * Vendor-neutral by name on purpose: it decides a subscription question, and
 * survived the move from Explo to Luzmo unchanged.
 */
export function subscriptionRequiresUpgradeToAccessAnalytics(
  subscription: CondensedSubscription,
): boolean {
  return (
    subscription.subscription_plan === "SHIPPER_TRIAL" ||
    subscription.subscription_plan === "SHIPPER_FREE_TIER" ||
    subscription.subscription_plan === "SHIPPER_SHIP_A" ||
    subscription.subscription_plan === "SHIPPER_SHIP_B" ||
    subscription.subscription_plan === "SHIPPER_SHIP_C"
  );
}

export function isNonPayingShipperSubscription(
  subscription: CondensedSubscription,
): boolean {
  return (
    subscription.subscription_plan === "SHIPPER_TRIAL" ||
    subscription.subscription_plan === "SHIPPER_FREE_TIER"
  );
}

export function isTrialShipperAwaitingPostingPermission(
  subscription: CondensedSubscription,
): boolean {
  return (
    subscription.subscription_plan === "SHIPPER_TRIAL" &&
    !subscription.has_shipment_posting_permission
  );
}

export function shipperSubscriptionTierWithoutPrivateShipments(
  subscription: CondensedSubscription,
): boolean {
  return (
    subscription.subscription_plan === "SHIPPER_FREE_TIER" ||
    subscription.subscription_plan === "SHIPPER_SHIP_A" ||
    subscription.subscription_plan === "SHIPPER_SHIP_B" ||
    subscription.subscription_plan === "SHIPPER_SHIP_C" ||
    shipperIsExpiredTrialShipper(subscription)
  );
}

export function getScheduleTypeOrDefaultForPoint(point: RoutePoint) {
  if (point?.schedule_type) {
    return point.schedule_type;
  }
  if (point.stop_operation === "PICKUP") {
    return "DATETIME";
  }
  if (point.stop_operation === "DROPOFF") {
    return "ASAP";
  }
  return "DATETIME";
}

function getNonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function findFirstFieldError(data: unknown): string | null {
  const asString = getNonEmptyString(data);
  if (asString !== null) {
    return asString;
  }
  if (Array.isArray(data)) {
    return (data as unknown[]).reduce<string | null>(
      (found, entry) => found ?? findFirstFieldError(entry),
      null,
    );
  }
  if (typeof data === "object" && data !== null) {
    return Object.values(data as Record<string, unknown>).reduce<string | null>(
      (found, entry) => found ?? findFirstFieldError(entry),
      null,
    );
  }
  return null;
}

/*
Reads the human readable reason out of an API error. The backend answers either
with a top level `message` or, when a serializer rejects the payload, with a map
of field name to error messages. Falling back to the field errors keeps a form
from showing a generic failure message when the API did explain itself, without
each caller having to name the fields its serializer can reject.
*/
export function getApiErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: unknown } } | null)?.response
    ?.data;
  const message =
    typeof data === "object" && data !== null
      ? getNonEmptyString((data as Record<string, unknown>).message)
      : null;

  return message ?? findFirstFieldError(data) ?? fallback;
}

export function extractAPIRequestErrorMessage(
  e: AxiosError,
  fallbackMessage: string,
  errorMessageKey?: string,
): string {
  const data = e?.response?.data;
  /*
  to safely check if an object has a direct (own) property without:
    being affected by the object's prototype,
    risking a runtime error if obj.hasOwnProperty doesn't exist.
  */

  const hasOwn = (obj: object, key: PropertyKey) => Object.hasOwn(obj, key);

  // first use errorMessageKey if available
  if (errorMessageKey && data?.[errorMessageKey]) {
    return data[errorMessageKey];
  }
  // error response may also have a message field, use it if available
  if (hasOwn(data ?? {}, "message")) {
    return data.message;
  }
  // lastly use the fallback message if no specific error message is available
  return fallbackMessage;
}

export function buildCarrierProfileEmergencyContactInfoURL(companyId: number) {
  return `${getCompanyProfilePath(companyId)}${EVOTRUX_URL_HASH.CARRIER_EMERGENCY_CONTACT}`;
}

export function carrierHasBookingWriteAccess(
  shipment?: CarrierCondensedShipment,
): boolean {
  return (
    shipment?.booked_by_carrier === true ||
    shipment?.carrier_access_type === "SHARED_BOOKING_READ_AND_WRITE"
  );
}

const BOOKED_OR_LATER_SHIPMENT_STATUSES: ShipmentStatusValue[] = [
  "BOOKED",
  "PICKED_UP",
  "DELIVERED",
  "BOOKING_CANCELLED",
];

export function carrierHasLostShipment(
  shipment?: CarrierCondensedShipment,
): boolean {
  if (!shipment) {
    return false;
  }
  return (
    BOOKED_OR_LATER_SHIPMENT_STATUSES.includes(shipment.shipment_status) &&
    !carrierHasBookingWriteAccess(shipment) &&
    !shipment.is_load_assignment
  );
}

export function appendQueryParamToUrl(
  url: string,
  key: string,
  value: string,
): string {
  const urlObj = new URL(url, window.location.origin);
  urlObj.searchParams.set(key, value);

  // Return the full URL if it has a different origin, otherwise return path + search + hash
  if (urlObj.origin === window.location.origin) {
    return `${urlObj.pathname}${urlObj.search}${urlObj.hash}`;
  }
  return urlObj.toString();
}

export function scrollMainContentToTop(delayMs = 100): void {
  setTimeout(() => {
    document
      .getElementById(MAIN_CONTENT_ELEMENT_ID)
      ?.scrollTo({ top: 0, behavior: "smooth" });
  }, delayMs);
}

export function formatPhoneNumber(e164: string): string {
  const parsed = parsePhoneNumberFromString(e164);
  return parsed ? parsed.formatInternational() : e164;
}

export function shipmentHasBeenDelivered(shipment: {
  delivery: ShipmentDelivery | null;
}): boolean {
  return shipment.delivery?.delivered_at != null;
}

let nextClientId = 0;
export function generateClientId(): string {
  nextClientId += 1;
  return String(nextClientId);
}

export function makeKeyActivationHandler(
  handler: VoidFunction,
): (e: KeyboardEvent) => void {
  return (e) => {
    if (e.key === "Enter" || e.key === " ") handler();
  };
}

const MAX_INITIALS = 2;

/**
 * "Acme Trucking Co" -> "AT". Used for avatar and logo monogram fallbacks.
 */
export function getInitialsFromName(name: string): string {
  const trimmed = name.trim();
  if (trimmed.length === 0) return "";
  return trimmed
    .split(/\s+/)
    .slice(0, MAX_INITIALS)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
}
